import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  varchar,
  jsonb,
  index,
  date,
  check,
  unique,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { PlanType } from "./planTypes";
import { plans, planDocuments } from "./plans";
import { clients, carriers } from "./business";

export const commissions = pgTable("commissions", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "restrict" }),
  carrierId: integer("carrier_id").references(() => carriers.id, { onDelete: "set null" }),
  month: varchar("month", { length: 7 }).notNull(), // YYYY-MM format - The period this commission is FOR
  accountingMonth: varchar("accounting_month", { length: 7 }), // YYYY-MM format - When this commission was RECORDED/PAID
  projectedPremium: decimal("projected_premium", {
    precision: 10,
    scale: 2,
  }),
  actualPremium: decimal("actual_premium", {
    precision: 10,
    scale: 2,
  }),
  premiumAmount: decimal("premium_amount", {
    precision: 10,
    scale: 2,
  }).notNull(), // Legacy field - kept for compatibility
  commissionRate: decimal("commission_rate", {
    precision: 5,
    scale: 2,
  }).notNull(),
  commissionCalculationType: text("commission_calculation_type")
    .notNull()
    .default("percentage"),
  // Used for FLAT/PEPM/PMPM: dollar amount per month/unit.
  commissionValue: decimal("commission_value", {
    precision: 10,
    scale: 2,
  }),
  projectedCommission: decimal("projected_commission", {
    precision: 10,
    scale: 2,
  }),
  actualCommission: decimal("actual_commission", {
    precision: 10,
    scale: 2,
  }),
  commissionAmount: decimal("commission_amount", {
    precision: 10,
    scale: 2,
  }).notNull(), // Legacy field - kept for compatibility
  status: text("status").notNull().default("pending"), // pending, in_progress, complete
  isPaid: boolean("is_paid").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const commissionRules = pgTable("commission_rules", {
  id: serial("id").primaryKey(),
  productType: text("product_type").notNull(), // health, dental, vision, life, disability, voluntary
  commissionRate: decimal("commission_rate", {
    precision: 5,
    scale: 2,
  }).notNull(), // percentage rate like 8.00 for 8%
  commissionType: text("commission_type").notNull(), // First Year Premium, Annual Premium
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const quotes = pgTable("quotes", {
  id: serial("id").primaryKey(),
  companyName: text("company_name").notNull(),
  employeeCount: integer("employee_count").notNull(),
  planType: text("plan_type").notNull(),
  monthlyPremium: decimal("monthly_premium", { precision: 10, scale: 2 }),
  status: text("status").notNull().default("draft"), // draft, sent, accepted, rejected
  createdAt: timestamp("created_at").defaultNow(),
  validUntil: timestamp("valid_until"),
  // New fields to support wizard draft persistence
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"),
  prospectId: integer("prospect_id"),
  effectiveDate: timestamp("effective_date"),
  currentStep: integer("current_step").default(1),
  wizardState: jsonb("wizard_state"),
  updatedAt: timestamp("updated_at").defaultNow(),
  // Type field to distinguish quotes from RFPs (11.2)
  type: text("type").default("quote"), // 'quote' or 'rfp'
  // Metadata fields for filtering and reporting (11.3)
  coverageTypes: text("coverage_types").array(), // array of selected coverage types
  totalMonthlyPremium: decimal("total_monthly_premium", { precision: 10, scale: 2 }),
  totalAnnualPremium: decimal("total_annual_premium", { precision: 10, scale: 2 }),
  carrierIds: integer("carrier_ids").array(), // array of selected carrier IDs
  // Shareable quote link fields
  shareToken: varchar("share_token", { length: 64 }).unique(), // unique token for public access
  shareExpiresAt: timestamp("share_expires_at"), // expiration date for share link
  proposalSettings: jsonb("proposal_settings"), // visual settings for the proposal (template, colors, etc.)
});

// RFP (Request for Proposal) tracking
export const rfps = pgTable("rfps", {
  id: serial("id").primaryKey(),
  quoteId: integer("quote_id").notNull().references(() => quotes.id, { onDelete: "cascade" }),
  coverageType: text("coverage_type").notNull(), // dental, vision, life, disability
  parameters: jsonb("parameters").notNull(), // RFP-specific parameters
  status: text("status").notNull().default("draft"), // draft, sent, responded, expired
  sentAt: timestamp("sent_at"),
  expiresAt: timestamp("expires_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// RFP to Carrier mapping (many-to-many)
export const rfpCarriers = pgTable("rfp_carriers", {
  id: serial("id").primaryKey(),
  rfpId: integer("rfp_id").notNull(), // references rfps.id
  carrierId: integer("carrier_id").notNull(), // references carriers.id
  contactId: integer("contact_id"), // references directoryContacts.id - the specific contact person
  emailId: integer("email_id"), // references emails.id - tracks sent email
  responseReceived: boolean("response_received").default(false),
  responseDate: timestamp("response_date"),
  responseNotes: text("response_notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Files a broker attaches to an RFP before sending it.
//
// Held against the QUOTE, not the client, so a file attached only to be emailed
// does not appear in that client's Documents. It survives saving and resuming a
// draft, and afterwards records what actually went out with the request.
//
// Not planFormAttachments, which links a plan to enrolment paperwork. Benefit
// summaries live in planDocuments (docType sbc|summary) on local disk, and can
// be referenced from here later rather than copied.
export const rfpAttachments = pgTable(
  "rfp_attachments",
  {
    id: serial("id").primaryKey(),
    quoteId: integer("quote_id")
      .notNull()
      .references(() => quotes.id, { onDelete: "cascade" }),
    /**
     * Who owns the bytes, which decides what removing an attachment may delete
     * and where the send reads it from.
     *
     * "upload"        this RFP owns the file. It lives in S3 under s3Key, and
     *                 removing the attachment deletes it.
     * "plan_document" an SBC or summary owned by a plan, referenced not copied.
     *                 Bytes stay on local disk under planDocuments.filePath,
     *                 and removing the attachment must leave them alone. A copy
     *                 would go stale the moment the plan's document is replaced.
     */
    source: text("source").notNull().default("upload"),
    /** What the broker sees, and the filename the carrier receives. */
    fileName: text("file_name").notNull(),
    /** Set for an upload. Null for a referenced document, whose bytes stay put. */
    s3Key: text("s3_key"),
    /** Set when source is "plan_document": the SBC or summary being referenced. */
    planDocumentId: integer("plan_document_id").references(() => planDocuments.id, {
      onDelete: "cascade",
    }),
    fileSize: integer("file_size").notNull(),
    mimeType: text("mime_type").notNull(),
    uploadedBy: varchar("uploaded_by"), // references users.id
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    quoteIdIdx: index("rfp_attachments_quote_id_idx").on(table.quoteId),
  }),
);

// Quote Plans Junction Table - links quotes to their selected plans
export const quotePlans = pgTable(
  "quote_plans",
  {
    id: serial("id").primaryKey(),
    quoteId: integer("quote_id").notNull().references(() => quotes.id, { onDelete: "cascade" }),
    planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
    coverageType: text("coverage_type").notNull(), // health, dental, vision, life, disability
    wordBrownId: text("word_brown_id"), // optional reference to Word & Brown plan ID
    addedAt: timestamp("added_at").defaultNow(),
    removedAt: timestamp("removed_at"), // soft delete timestamp
    isActive: boolean("is_active").default(true),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    quoteIdIdx: index("quote_plans_quote_id_idx").on(table.quoteId),
    planIdIdx: index("quote_plans_plan_id_idx").on(table.planId),
  })
);

// Define premium summary schema for persisted calculations
const premiumSummarySchema = z.object({
  totalMonthlyPremium: z.number().default(0),
  totalAnnualPremium: z.number().default(0),
  totalEmployeePortion: z.number().default(0),
  totalEmployerPortion: z.number().default(0),
  planBreakdowns: z.array(z.any()).default([]),
  byCoverageType: z.record(z.any()).default({}),
  // Min/max monthly across plan options — shown on the shared proposal.
  premiumRangeMin: z.number().optional(),
  premiumRangeMax: z.number().optional(),
}).optional().nullable();

// Employer contribution strategy chosen in the wizard: employer share of the
// employee's own premium and of the dependents' premium (percentage of that
// portion, or a flat monthly dollar amount toward it).
export const quoteContributionSchema = z.object({
  type: z.enum(["percentage", "fixed"]).default("percentage"),
  employee: z.number().min(0).default(0),
  dependent: z.number().min(0).default(0),
});

// Define a stricter schema for persisted wizard state
export const quoteWizardStateSchema = z.object({
  mode: z.enum(["quote", "rfp"]).optional(),
  clientType: z.enum(["client", "prospect"]).nullable().optional(),
  selectedEntityId: z.number().int().nullable().optional(),
  effectiveDate: z.coerce.date().nullable().optional(),
  coverageTypes: z.array(z.nativeEnum(PlanType)).default([]),
  selectedEmployees: z.array(z.number().int()).default([]),
  selectedDependents: z.array(z.number().int()).default([]),
  selectedCarriers: z
    .record(z.nativeEnum(PlanType), z.array(z.number().int()))
    .default({}) as unknown as z.ZodType<Record<string, number[]>>, // satisfy TS for record key typing
  selectedPlans: z
    .record(z.nativeEnum(PlanType), z.array(z.union([z.number().int(), z.object({}).passthrough()])))
    .default({}) as unknown as z.ZodType<Record<string, (number | object)[]>>,
  // One current plan per coverage type (not a list, unlike selectedPlans) —
  // what the client's group already has, for the Step 5 comparison.
  currentPlans: z
    .record(z.nativeEnum(PlanType), z.union([z.number().int(), z.object({}).passthrough()]).nullable())
    .default({}) as unknown as z.ZodType<Record<string, number | object | null>>,
  compareCurrentEnabled: z
    .record(z.nativeEnum(PlanType), z.boolean())
    .default({}) as unknown as z.ZodType<Record<string, boolean>>,
  rfpParameters: z.record(z.any()).default({}).optional(),
  // RFP only: send to each carrier's own contact, or to one or two general
  // agents who approach the carriers instead. Zod strips unknown keys, so a
  // field missing from this schema never reaches the DB however carefully the
  // client sends it — that is what silently lost the GA choice on resume.
  rfpSendVia: z.enum(["carriers", "ga"]).optional(),
  // Ids of general_agencies (the firms), not of their contacts. Every sales
  // contact at a picked firm is emailed, so the list is resolved at send time.
  rfpGeneralAgencyIds: z.array(z.number().int()).default([]).optional(),
  // "generalAgencyId:carrierId" pairs: which carriers each GA is asked for.
  rfpGeneralAgencyAsks: z.array(z.string()).default([]).optional(),
  // When proposals are due. Date-only ("2026-09-30"), so it names the same day
  // in every timezone. Absent means two weeks from the send.
  rfpRespondBy: z.string().optional().nullable(),
  // The broker's own wording, replacing the generated prose. Empty or absent
  // keeps the template. One set for the whole RFP: wording only one firm sees
  // belongs in the per-recipient note, which already exists.
  //
  // Prose only. The facts, the carrier list, the coverage sections and the
  // recipient's own name differ per recipient and are never overridable.
  // rfpEmailGreeting is the salutation WORD ("Dear"), never the name.
  rfpEmailGreeting: z.string().optional().nullable(),
  rfpEmailIntro: z.string().optional().nullable(),
  rfpEmailClosing: z.string().optional().nullable(),
  // One line each. The sender details behind it are resolved server-side.
  rfpEmailSignature: z.string().optional().nullable(),
  highestCompletedStep: z.number().int().default(0),
  premiumSummary: premiumSummarySchema,
  // Same shape, for the current plan — powers "Your Current Plan" on the
  // public share-link page.
  currentPremiumSummary: premiumSummarySchema.optional().nullable(),
  confirmedRatingArea: z.union([z.string(), z.object({}).passthrough()]).optional().nullable(),
  contribution: quoteContributionSchema.optional().nullable(),
  proposalSettings: z.object({}).passthrough().optional().nullable(),
});

// Insert schemas
export const insertCommissionSchema = createInsertSchema(commissions, {
  // Accept number or string for decimal and coerce to string
  projectedPremium: z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === null ? null : v.toString())),
  actualPremium: z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === null ? null : v.toString())),
  premiumAmount: z
    .union([z.string(), z.number()])
    .transform((v) => v.toString()),
  commissionRate: z
    .union([z.string(), z.number()])
    .transform((v) => v.toString()),
  commissionValue: z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === null ? null : v.toString())),
  projectedCommission: z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === null ? null : v.toString())),
  actualCommission: z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === null ? null : v.toString())),
  commissionAmount: z
    .union([z.string(), z.number()])
    .transform((v) => v.toString()),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertCommissionRuleSchema = createInsertSchema(commissionRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertQuoteSchema = createInsertSchema(quotes, {
  // Accept number or string for decimal and coerce to string
  monthlyPremium: z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === null ? null : v.toString())),
  // Accept ISO strings or Date for timestamps
  effectiveDate: z.coerce.date().optional().nullable(),
  validUntil: z.coerce.date().optional().nullable(),
  updatedAt: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  // Allow nullable linkage ids
  clientId: z.number().int().optional().nullable(),
  prospectId: z.number().int().optional().nullable(),
  // Allow partial/draft fields
  currentStep: z.number().int().optional(),
  wizardState: quoteWizardStateSchema.optional().nullable(),
}).omit({
  id: true,
  createdAt: true,
});

export const insertRfpSchema = createInsertSchema(rfps).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertRfpCarrierSchema = createInsertSchema(rfpCarriers).omit({
  id: true,
  createdAt: true,
});

export const insertQuotePlanSchema = createInsertSchema(quotePlans).omit({
  id: true,
  createdAt: true,
  addedAt: true,
});

// Commission Split Profiles
export const commissionSplitProfiles = pgTable(
  "commission_split_profiles",
  {
    id: serial("id").primaryKey(),
    createdBy: text("created_by").notNull(),
    name: text("name").notNull(),
    splitType: text("split_type").notNull(), // 'agency_agent' or 'primary_secondary'
    agencyPercentage: decimal("agency_percentage", { precision: 5, scale: 2 }),
    agentPercentage: decimal("agent_percentage", { precision: 5, scale: 2 }),
    primaryPercentage: decimal("primary_percentage", { precision: 5, scale: 2 }),
    secondaryPercentage: decimal("secondary_percentage", { precision: 5, scale: 2 }),
    description: text("description"),
    isMilestoneBased: boolean("is_milestone_based").default(false).notNull(),
    isDefault: boolean("is_default").default(false).notNull(),
    isGlobal: boolean("is_global").default(true).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    splitTypeIdx: index("commission_split_profiles_split_type_idx").on(table.splitType),
    createdByIdx: index("commission_split_profiles_created_by_idx").on(table.createdBy),
    checkAgencyAgentSplit: check("check_agency_agent_split", sql`(${table.splitType} = 'agency_agent' AND ${table.agencyPercentage} + ${table.agentPercentage} = 100) OR ${table.splitType} != 'agency_agent'`),
    checkPrimarySecondarySplit: check("check_primary_secondary_split", sql`(${table.splitType} = 'primary_secondary' AND ${table.primaryPercentage} + ${table.secondaryPercentage} = 100) OR ${table.splitType} != 'primary_secondary'`),
  })
);

export const insertCommissionSplitProfileSchema = createInsertSchema(commissionSplitProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Commission Split Milestones — year-based percentage tiers for milestone-based profiles
export const commissionSplitMilestones = pgTable(
  "commission_split_milestones",
  {
    id: serial("id").primaryKey(),
    splitProfileId: integer("split_profile_id").notNull().references(() => commissionSplitProfiles.id, { onDelete: "cascade" }),
    yearNumber: integer("year_number").notNull(), // 1, 2, 3...
    isYearAndBeyond: boolean("is_year_and_beyond").default(false).notNull(), // true = "year N+"
    agencyPercentage: decimal("agency_percentage", { precision: 5, scale: 2 }),
    agentPercentage: decimal("agent_percentage", { precision: 5, scale: 2 }),
    primaryPercentage: decimal("primary_percentage", { precision: 5, scale: 2 }),
    secondaryPercentage: decimal("secondary_percentage", { precision: 5, scale: 2 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    profileIdx: index("milestones_profile_idx").on(table.splitProfileId),
    uniqueYear: unique().on(table.splitProfileId, table.yearNumber),
  })
);

export const insertCommissionSplitMilestoneSchema = createInsertSchema(commissionSplitMilestones).omit({
  id: true,
  createdAt: true,
});

export type CommissionSplitMilestone = typeof commissionSplitMilestones.$inferSelect;
export type InsertCommissionSplitMilestone = z.infer<typeof insertCommissionSplitMilestoneSchema>;

// Carrier Commission Statements
export const carrierCommissionStatements = pgTable(
  "carrier_commission_statements",
  {
    id: serial("id").primaryKey(),
    agencyId: varchar("agency_id").notNull(), // references agencies.id
    carrierId: integer("carrier_id"), // references carriers.id (nullable for manual uploads)
    statementMonth: varchar("statement_month", { length: 7 }).notNull(), // YYYY-MM format
    uploadedBy: varchar("uploaded_by").notNull(), // user id who uploaded
    fileName: text("file_name").notNull(),
    fileSize: integer("file_size").notNull(), // in bytes
    filePath: text("file_path").notNull(), // S3 key
    mimeType: text("mime_type").notNull(),
    status: text("status").notNull().default("pending"), // pending, processing, reconciled, error
    totalCommission: decimal("total_commission", { precision: 12, scale: 2 }),
    notes: text("notes"),
    uploadedAt: timestamp("uploaded_at").defaultNow().notNull(),
    processedAt: timestamp("processed_at"),
    reconciledAt: timestamp("reconciled_at"),
    metadata: jsonb("metadata"), // additional data like line item count, etc.
  },
  (table) => ({
    agencyIdIdx: index("carrier_statements_agency_id_idx").on(table.agencyId),
    carrierIdIdx: index("carrier_statements_carrier_id_idx").on(table.carrierId),
    statementMonthIdx: index("carrier_statements_statement_month_idx").on(table.statementMonth),
    statusIdx: index("carrier_statements_status_idx").on(table.status),
  })
);

export const insertCarrierCommissionStatementSchema = createInsertSchema(carrierCommissionStatements, {
  totalCommission: z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === null ? null : v.toString())),
  uploadedAt: z.coerce.date().optional(),
  processedAt: z.coerce.date().optional().nullable(),
  reconciledAt: z.coerce.date().optional().nullable(),
}).omit({
  id: true,
});

// Quote Versions - Immutable snapshots of quotes for client viewing
export const quoteVersions = pgTable(
  "quote_versions",
  {
    id: serial("id").primaryKey(),
    quoteId: integer("quote_id").notNull().references(() => quotes.id, { onDelete: "cascade" }),
    versionNumber: integer("version_number").notNull().default(1),
    status: text("status").notNull().default("draft"), // draft, published, superseded
    publishedAt: timestamp("published_at"),
    publishedBy: varchar("published_by"), // user id who published
    snapshotJson: jsonb("snapshot_json").notNull(), // immutable snapshot of plans, rates, census, etc.
    notes: text("notes"), // internal notes about this version
    changeLog: text("change_log"), // what changed from previous version
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    quoteIdIdx: index("quote_versions_quote_id_idx").on(table.quoteId),
    statusIdx: index("quote_versions_status_idx").on(table.status),
  })
);

// Published Quote Links - Shareable links for clients to view quote versions
export const publishedQuoteLinks = pgTable(
  "published_quote_links",
  {
    id: serial("id").primaryKey(),
    quoteVersionId: integer("quote_version_id").notNull(), // references quoteVersions.id
    publicToken: varchar("public_token", { length: 64 }).notNull().unique(),
    expiresAt: timestamp("expires_at"),
    viewPermissions: jsonb("view_permissions").default({}), // allow_strategy_selection, allow_acceptance, allow_filtering
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    tokenIdx: index("published_quote_links_token_idx").on(table.publicToken),
    versionIdIdx: index("published_quote_links_version_id_idx").on(table.quoteVersionId),
  })
);

// Quote Acceptances - Client acceptance records tied to specific versions
export const quoteAcceptances = pgTable(
  "quote_acceptances",
  {
    id: serial("id").primaryKey(),
    quoteVersionId: integer("quote_version_id").notNull(), // references quoteVersions.id
    status: text("status").notNull().default("pending"), // pending, accepted, declined, expired
    selectedScenarioId: varchar("selected_scenario_id"), // which strategy scenario chosen
    selectedStrategyJson: jsonb("selected_strategy_json"), // full strategy details
    selectedBasePlanIds: integer("selected_base_plan_ids").array(), // base plans if applicable
    selectedPreferredPlans: integer("selected_preferred_plans").array(), // preferred plans from quoted set
    selectedPlansJson: jsonb("selected_plans_json"), // client-selected plans by coverage type
    signerName: varchar("signer_name", { length: 255 }),
    signerTitle: varchar("signer_title", { length: 255 }),
    signerEmail: varchar("signer_email", { length: 255 }),
    acceptedAt: timestamp("accepted_at"),
    ipAddress: varchar("ip_address", { length: 45 }),
    userAgent: text("user_agent"),
    acknowledgementsJson: jsonb("acknowledgements_json"), // checkboxes/confirmations
    // Null when the client accepted through the public link. Set when a broker
    // recorded an agreement reached off the platform (a phone call), in which
    // case there is no signer and no contribution strategy to read.
    recordedByUserId: varchar("recorded_by_user_id"),
    comments: text("comments"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    versionIdIdx: index("quote_acceptances_version_id_idx").on(table.quoteVersionId),
    statusIdx: index("quote_acceptances_status_idx").on(table.status),
  })
);

// Broker Config for Client Quote - Defines what clients can do with a quote
export const brokerQuoteConfigs = pgTable(
  "broker_quote_configs",
  {
    id: serial("id").primaryKey(),
    quoteVersionId: integer("quote_version_id").notNull(), // references quoteVersions.id
    allowedStrategyTypes: text("allowed_strategy_types").array().default([]), // percent, flat, reference_to_base
    allowedRangesByTier: jsonb("allowed_ranges_by_tier").default({}), // min/max by tier
    allowBasePlanReference: boolean("allow_base_plan_reference").default(false),
    allowClientCustomStrategy: boolean("allow_client_custom_strategy").default(false),
    allowPreferredPlanSelection: boolean("allow_preferred_plan_selection").default(false),
    allowEmployeeRatesView: boolean("allow_employee_rates_view").default(true),
    allowDependentsDetailView: boolean("allow_dependents_detail_view").default(true),
    predefinedScenarios: jsonb("predefined_scenarios").default([]), // Scenario A/B/C configs
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    versionIdIdx: index("broker_quote_configs_version_id_idx").on(table.quoteVersionId),
  })
);

// Client Strategy Selections - Saves client's draft strategy selections before acceptance
export const clientStrategySelections = pgTable(
  "client_strategy_selections",
  {
    id: serial("id").primaryKey(),
    publishedLinkId: integer("published_link_id").notNull(), // references publishedQuoteLinks.id
    scenarioId: varchar("scenario_id"),
    strategyJson: jsonb("strategy_json"),
    basePlanIds: integer("base_plan_ids").array(),
    preferredPlanIds: integer("preferred_plan_ids").array(),
    filterPreferences: jsonb("filter_preferences"), // saved filter state
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    linkIdIdx: index("client_strategy_selections_link_id_idx").on(table.publishedLinkId),
  })
);

// Insert schemas for new tables
export const insertQuoteVersionSchema = createInsertSchema(quoteVersions, {
  publishedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertPublishedQuoteLinkSchema = createInsertSchema(publishedQuoteLinks, {
  expiresAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
}).omit({
  id: true,
  createdAt: true,
});

export const insertQuoteAcceptanceSchema = createInsertSchema(quoteAcceptances, {
  acceptedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertBrokerQuoteConfigSchema = createInsertSchema(brokerQuoteConfigs, {
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertClientStrategySelectionSchema = createInsertSchema(clientStrategySelections, {
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type Commission = typeof commissions.$inferSelect;
export type InsertCommission = z.infer<typeof insertCommissionSchema>;
export type CommissionRule = typeof commissionRules.$inferSelect;
export type InsertCommissionRule = z.infer<typeof insertCommissionRuleSchema>;
export type Quote = typeof quotes.$inferSelect;
export type InsertQuote = z.infer<typeof insertQuoteSchema>;
export type QuoteWizardState = z.infer<typeof quoteWizardStateSchema>;
export type Rfp = typeof rfps.$inferSelect;
export type InsertRfp = z.infer<typeof insertRfpSchema>;
export type RfpCarrier = typeof rfpCarriers.$inferSelect;
export type RfpAttachment = typeof rfpAttachments.$inferSelect;
export type InsertRfpCarrier = z.infer<typeof insertRfpCarrierSchema>;
export type QuotePlan = typeof quotePlans.$inferSelect;
export type InsertQuotePlan = z.infer<typeof insertQuotePlanSchema>;
export type CommissionSplitProfile = typeof commissionSplitProfiles.$inferSelect;
export type InsertCommissionSplitProfile = z.infer<typeof insertCommissionSplitProfileSchema>;
export type CarrierCommissionStatement = typeof carrierCommissionStatements.$inferSelect;
export type InsertCarrierCommissionStatement = z.infer<typeof insertCarrierCommissionStatementSchema>;
export type QuoteVersion = typeof quoteVersions.$inferSelect;
export type InsertQuoteVersion = z.infer<typeof insertQuoteVersionSchema>;
export type PublishedQuoteLink = typeof publishedQuoteLinks.$inferSelect;
export type InsertPublishedQuoteLink = z.infer<typeof insertPublishedQuoteLinkSchema>;
export type QuoteAcceptance = typeof quoteAcceptances.$inferSelect;
export type InsertQuoteAcceptance = z.infer<typeof insertQuoteAcceptanceSchema>;
export type BrokerQuoteConfig = typeof brokerQuoteConfigs.$inferSelect;
export type InsertBrokerQuoteConfig = z.infer<typeof insertBrokerQuoteConfigSchema>;
export type ClientStrategySelection = typeof clientStrategySelections.$inferSelect;
export type InsertClientStrategySelection = z.infer<typeof insertClientStrategySelectionSchema>;

// RFP Proposal Files — uploaded carrier responses to an RFP
export const rfpProposals = pgTable("rfp_proposals", {
  id: serial("id").primaryKey(),
  rfpId: integer("rfp_id").notNull().references(() => rfps.id, { onDelete: "cascade" }),
  quoteId: integer("quote_id").notNull().references(() => quotes.id, { onDelete: "cascade" }),
  carrierId: integer("carrier_id").references(() => carriers.id, { onDelete: "set null" }),
  carrierName: text("carrier_name"),
  planVariant: text("plan_variant"),
  fileName: text("file_name").notNull(),
  fileType: text("file_type").notNull(), // pdf, xlsx, docx, xls, doc
  s3Key: text("s3_key").notNull(),
  fileSize: integer("file_size"),
  extractedText: text("extracted_text"),
  status: text("status").notNull().default("uploaded"), // uploaded, processing, processed, error
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// RFP Compiled Proposals — AI-consolidated output from carrier proposals (one per quote)
export const rfpCompiledProposals = pgTable("rfp_compiled_proposals", {
  id: serial("id").primaryKey(),
  quoteId: integer("quote_id").notNull().unique().references(() => quotes.id, { onDelete: "cascade" }),
  compiledContent: jsonb("compiled_content"), // holds recommendation, brokerNotes, agentInfo, census, hiddenCarriers
  executiveSummary: text("executive_summary"),
  analysisStatus: text("analysis_status"), // processing, complete, error — async analyze job state
  analysisError: text("analysis_error"),
  analysisInputHash: text("analysis_input_hash"), // fingerprint of input files — skip re-analyze when unchanged
  shareToken: varchar("share_token", { length: 64 }).unique(),
  shareExpiresAt: timestamp("share_expires_at"),
  revokedAt: timestamp("revoked_at"), // share link killed before expiry — public endpoints 410 when set

  status: text("status").notNull().default("draft"), // draft, presented, sold, accepted
  presentedAt: timestamp("presented_at"),
  soldAt: timestamp("sold_at"),
  // Client acceptance fields
  acceptedAt: timestamp("accepted_at"),
  acceptedByName: varchar("accepted_by_name", { length: 255 }),
  acceptedByTitle: varchar("accepted_by_title", { length: 255 }),
  acceptedByEmail: varchar("accepted_by_email", { length: 255 }),
  selectedCoverages: jsonb("selected_coverages"), // { coverageType: { selected: bool, carrier: string } }
  acceptanceIp: varchar("acceptance_ip", { length: 45 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertRfpProposalSchema = createInsertSchema(rfpProposals).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertRfpCompiledProposalSchema = createInsertSchema(rfpCompiledProposals).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type RfpProposal = typeof rfpProposals.$inferSelect;
export type InsertRfpProposal = z.infer<typeof insertRfpProposalSchema>;
export type RfpCompiledProposal = typeof rfpCompiledProposals.$inferSelect;
export type InsertRfpCompiledProposal = z.infer<typeof insertRfpCompiledProposalSchema>;

// Structural schema for the `compiledContent` jsonb (AI output + broker edits).
// Lenient on purpose: .passthrough() + optional/default everywhere, so only real
// structural breakage is rejected — sparse/extra fields from the model pass.
const rfpCarrierSchema = z
  .object({
    name: z.string(),
    fields: z.record(z.any()).default({}),
    _manualFields: z.record(z.any()).optional(),
    proposalNotes: z.array(z.string()).optional(),
  })
  .passthrough();
const rfpCoverageLineSchema = z
  .object({
    type: z.string(),
    label: z.string().optional(),
    carriers: z.array(rfpCarrierSchema).default([]),
    census: z.any().optional(),
  })
  .passthrough();
export const rfpCompiledContentSchema = z
  .object({
    companyName: z.string().optional(),
    analysisDate: z.string().optional(),
    coverageLines: z.array(rfpCoverageLineSchema).default([]),
    recommendation: z.string().optional(),
    brokerNotes: z.string().optional(),
    agentInfo: z.record(z.any()).optional(),
    hiddenCarriers: z.array(z.string()).optional(),
  })
  .passthrough();
export type RfpCompiledContent = z.infer<typeof rfpCompiledContentSchema>;
